import { useMemo, useState } from 'react'
import './CashFlowSankey.css'

const fmt = n => '$' + Math.round(n || 0).toLocaleString('es-AR')

// SaaS distribution percentages (agreed: Agustín mayor parte por desarrollo)
const SAAS_DIST = { agustin: 0.40, martin: 0.25, pipi: 0.25, reserva: 0.10 }

// armado3dPct  = % del precio de venta HW → Agustín (carcasas 3D + diseño)
// armadoElecPct = % del precio de venta HW → Pipi (armado electrónico)
// ventaPct     = % del precio de venta HW → "Com. Venta" (quien vendió)
function buildDiagram(devRev, mrr, devCost, armado3dPct, armadoElecPct, ventaPct, reinvPct, txRate) {
  const totalIncome = devRev + mrr
  if (!totalIncome || totalIncome <= 0) return null

  const inventoryReserve = devCost

  // --- HW commissions (% of devRev, tomadas antes de impuestos) ---
  const armado3d  = devRev > 0 ? Math.round(devRev * armado3dPct  / 100) : 0
  const armadoElec = devRev > 0 ? Math.round(devRev * armadoElecPct / 100) : 0
  const ventaComm  = devRev > 0 ? Math.round(devRev * ventaPct     / 100) : 0

  // --- Taxes sobre lo que queda tras repos + comisiones HW ---
  const afterCosts = Math.max(0, totalIncome - inventoryReserve - armado3d - armadoElec - ventaComm)
  const tax = Math.max(0, Math.round(afterCosts * (txRate / 100)))

  // Split taxes HW vs SaaS proporcionalmente al ingreso
  const hwRatio = totalIncome > 0 ? devRev / totalIncome : 0
  const hwTax   = Math.round(tax * hwRatio)
  const saasTax = tax - hwTax

  // --- HW net → ÷3 socios (Agustín, Martín, Pipi) ---
  const hwAfterTax = Math.max(0, devRev - inventoryReserve - armado3d - armadoElec - ventaComm - hwTax)
  const hwReinv    = Math.round(hwAfterTax * reinvPct / 100)
  const hwFree     = Math.max(0, hwAfterTax - hwReinv)
  const hwPer      = Math.round(hwFree / 3)

  // --- SaaS distribution ---
  const saasNet = Math.max(0, mrr - saasTax)
  const sa = Object.fromEntries(
    Object.entries(SAAS_DIST).map(([k, v]) => [k, Math.round(saasNet * v)])
  )

  // --- Build source nodes ---
  const sources = [
    devRev > 0 && { key: 'hw', name: 'Vta. Hardware', value: devRev, color: '#3b82f6' },
    mrr > 0    && { key: 'saas', name: 'Suscripciones', value: mrr,   color: '#8b5cf6' },
  ].filter(Boolean)

  if (sources.length === 0) return null

  // --- Build destination nodes ---
  const reinvTotal = hwReinv + sa.reserva
  const dests = [
    inventoryReserve > 0                    && { name: 'Repos. Inventario', value: inventoryReserve,         color: '#f59e0b' },
    tax > 0                                 && { name: 'Impuestos',         value: tax,                      color: '#ef4444' },
    reinvTotal > 0                          && { name: 'Reinversión',       value: reinvTotal,               color: '#a855f7' },
    ventaComm > 0                           && { name: 'Com. Venta',        value: ventaComm,                color: '#ec4899' },
    (armado3d + hwPer + sa.agustin) > 0    && { name: 'Agustín',           value: armado3d + hwPer + sa.agustin,  color: '#10b981' },
    (armadoElec + hwPer + sa.pipi) > 0     && { name: 'Pipi',              value: armadoElec + hwPer + sa.pipi,   color: '#14b8a6' },
    (hwPer + sa.martin) > 0               && { name: 'Martín',            value: hwPer + sa.martin,        color: '#06b6d4' },
  ].filter(Boolean)

  if (dests.length === 0) return null

  // --- Build links ---
  const destNames = dests.map(d => d.name)
  const makeLinks = (srcKey, pairs) =>
    pairs.filter(([, v]) => v > 0)
      .sort((a, b) => destNames.indexOf(a[0]) - destNames.indexOf(b[0]))
      .map(([dst, value]) => ({ src: srcKey, dst, value }))

  const hwLinks = makeLinks('hw', [
    ['Repos. Inventario', inventoryReserve],
    ['Impuestos',         hwTax],
    ['Reinversión',       hwReinv],
    ['Com. Venta',        ventaComm],
    ['Agustín',           armado3d + hwPer],
    ['Pipi',              armadoElec + hwPer],
    ['Martín',            hwPer],
  ])
  const saasLinks = makeLinks('saas', [
    ['Impuestos',   saasTax],
    ['Reinversión', sa.reserva],
    ['Agustín',     sa.agustin],
    ['Pipi',        sa.pipi],
    ['Martín',      sa.martin],
  ])
  const allLinks = [...hwLinks, ...saasLinks]

  // --- SVG Layout ---
  const H = 500, PAD = 30, NW = 14
  const SX = 145, DX = 1000 - 155
  const SGAP = 24, DGAP = 8

  const availH = H - PAD * 2
  const srcTotal = sources.reduce((a, s) => a + s.value, 0)
  const dstTotal = dests.reduce((a, d) => a + d.value, 0)
  const srcScale = (availH - (sources.length - 1) * SGAP) / srcTotal
  const dstScale = (availH - (dests.length - 1) * DGAP) / dstTotal
  const sc = Math.min(srcScale, dstScale)

  const srcTotalH = sources.reduce((a, s) => a + s.value * sc, 0) + (sources.length - 1) * SGAP
  let sy = PAD + (availH - srcTotalH) / 2
  const srcPos = sources.map(s => {
    const h = Math.max(20, s.value * sc)
    const node = { ...s, x: SX, y: sy, h, w: NW }
    sy += h + SGAP
    return node
  })

  const dstTotalH = dests.reduce((a, d) => a + Math.max(14, d.value * sc), 0) + (dests.length - 1) * DGAP
  let dy = PAD + (availH - dstTotalH) / 2
  const dstPos = dests.map(d => {
    const h = Math.max(14, d.value * sc)
    const node = { ...d, x: DX, y: dy, h, w: NW }
    dy += h + DGAP
    return node
  })

  // --- Generate SVG paths ---
  const srcOff = {}; sources.forEach(s => { srcOff[s.key] = 0 })
  const dstOff = {}; dests.forEach(d => { dstOff[d.name] = 0 })

  const paths = allLinks.map((link, i) => {
    const src = srcPos.find(s => s.key === link.src)
    const dst = dstPos.find(d => d.name === link.dst)
    if (!src || !dst) return null

    const lh = Math.max(1.5, link.value * sc)
    const y1 = src.y + srcOff[link.src]
    const y2 = dst.y + dstOff[link.dst]
    srcOff[link.src] += lh
    dstOff[link.dst] += lh

    const x1 = SX + NW
    const x2 = DX
    const cx1 = x1 + (x2 - x1) * 0.4
    const cx2 = x2 - (x2 - x1) * 0.4

    return {
      d: `M${x1},${y1} C${cx1},${y1} ${cx2},${y2} ${x2},${y2} L${x2},${y2 + lh} C${cx2},${y2 + lh} ${cx1},${y1 + lh} ${x1},${y1 + lh} Z`,
      color: src.color,
      value: link.value,
      srcName: src.name,
      dstName: link.dst,
      idx: i,
      pct: ((link.value / totalIncome) * 100).toFixed(1),
    }
  }).filter(Boolean)

  return { srcPos, dstPos, paths, totalIncome }
}

export default function CashFlowSankey({ cashAllocation, monthlySnapshots, armado3dPct, armadoElecPct, ventaPct, reinvestPct, taxRate }) {
  const [hovered, setHovered] = useState(null)
  const [selectedMonth, setSelectedMonth] = useState(-1) // -1 = current

  const months = monthlySnapshots || []
  const isCurrent = selectedMonth === -1 || selectedMonth >= months.length

  const diagram = useMemo(() => {
    if (isCurrent) {
      const { effectiveDevRev, effectiveMRR, inventoryReserve } = cashAllocation
      return buildDiagram(effectiveDevRev, effectiveMRR, inventoryReserve, armado3dPct, armadoElecPct, ventaPct, reinvestPct, taxRate)
    }
    const snap = months[selectedMonth]
    if (!snap) return null
    return buildDiagram(snap.devRev, snap.mrr, snap.devCost, armado3dPct, armadoElecPct, ventaPct, reinvestPct, taxRate)
  }, [cashAllocation, months, selectedMonth, armado3dPct, armadoElecPct, ventaPct, reinvestPct, taxRate, isCurrent])

  if (!diagram && months.length === 0) return null

  return (
    <div className="sankey-wrapper">
      {/* Month selector */}
      <div className="sankey-months">
        {months.map((m, i) => (
          <button key={i} className={`sankey-month-btn ${selectedMonth === i ? 'active' : ''}`} onClick={() => setSelectedMonth(i)}>
            {m.label}
          </button>
        ))}
        <button className={`sankey-month-btn current ${isCurrent ? 'active' : ''}`} onClick={() => setSelectedMonth(-1)}>
          Actual
        </button>
      </div>

      {diagram ? (
        <>
        <svg viewBox="0 0 1000 500" className="sankey-svg" preserveAspectRatio="xMidYMid meet">
        {/* Link paths */}
        {diagram.paths.map(p => (
          <path
            key={p.idx}
            d={p.d}
            fill={p.color}
            fillOpacity={hovered === null ? 0.28 : hovered === p.idx ? 0.6 : 0.06}
            stroke={hovered === p.idx ? p.color : 'none'}
            strokeWidth={hovered === p.idx ? 1.2 : 0}
            onMouseEnter={() => setHovered(p.idx)}
            onMouseLeave={() => setHovered(null)}
            style={{ cursor: 'pointer', transition: 'fill-opacity 0.2s, stroke-width 0.15s' }}
          />
        ))}

        {/* Source nodes */}
        {diagram.srcPos.map((n, i) => (
          <g key={`s${i}`}>
            <rect x={n.x} y={n.y} width={n.w} height={n.h} fill={n.color} rx={4} />
            <text x={n.x - 12} y={n.y + n.h / 2 - 9} textAnchor="end" fill="#e2e8f0" fontSize={14} fontWeight="600">
              {n.name}
            </text>
            <text x={n.x - 12} y={n.y + n.h / 2 + 9} textAnchor="end" fill="#94a3b8" fontSize={12}>
              {fmt(n.value)}
            </text>
          </g>
        ))}

        {/* Destination nodes */}
        {diagram.dstPos.map((n, i) => (
          <g key={`d${i}`}>
            <rect x={n.x} y={n.y} width={n.w} height={n.h} fill={n.color} rx={3} />
            <text x={n.x + n.w + 10} y={n.y + n.h / 2 - 6} textAnchor="start" fill="#e2e8f0" fontSize={13} fontWeight="500">
              {n.name}
            </text>
            <text x={n.x + n.w + 10} y={n.y + n.h / 2 + 10} textAnchor="start" fill={n.color} fontSize={12} fontWeight="600">
              {fmt(n.value)}
            </text>
          </g>
        ))}
      </svg>

      {/* Hover detail bar */}
      {hovered !== null && diagram.paths[hovered] && (
        <div className="sankey-detail">
          <span className="sankey-detail-dot" style={{ color: diagram.paths[hovered].color }}>●</span>
          <span>{diagram.paths[hovered].srcName} → {diagram.paths[hovered].dstName}:</span>
          <strong>{fmt(diagram.paths[hovered].value)}</strong>
          <span className="sankey-detail-pct">({diagram.paths[hovered].pct}%)</span>
        </div>
      )}
      </>) : (
        <div className="sankey-empty">Sin datos para este mes</div>
      )}
    </div>
  )
}
